import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { Banner } from '../components/Status';
import { useI18n, type MessageKey } from '../i18n';
import { TabTip } from './TabTip';
import { DOCK_TABS } from './tabTips';
import { useRepos } from '../repositories';
import { AddSheet } from './AddSheet';
import { useApp, type PanelKind } from './AppState';
import { Panels } from './Panels';
import { Link, useRouter } from './router';
import { AssistantButton } from '../assistant/AssistantButton';
import { AssistantOverlay } from '../assistant/AssistantOverlay';
import { Spotlight } from '../assistant/Spotlight';
import { ReportIssueDialog } from '../features/me/ReportIssueDialog';

/**
 * Language switch — visible on every screen, never inside Settings (section 14.1).
 * Rendered as a round icon showing the *other* language's short mark, so it
 * reads as a picture rather than a labelled bar.
 */
export function LanguageSwitch({ compact }: { compact?: boolean }) {
  const { lang, setLang, t } = useI18n();
  const repos = useRepos();
  const other = lang === 'en' ? 'he' : 'en';
  const mark = other === 'he' ? 'עב' : 'EN';
  return (
    <button
      type="button"
      className={`lang-switch ${compact ? 'is-compact' : ''}`}
      onClick={() => {
        setLang(other);
        void repos.auth.updatePreferredLang(other);
      }}
      aria-label={t('lang.switchLabel')}
      title={t('lang.switchLabel')}
      lang={other}
    >
      <span className="lang-mark" aria-hidden="true">{mark}</span>
      {!compact && <span className="faint" lang={lang}>{t('lang.current')}</span>}
    </button>
  );
}

/**
 * The dock: the family tree, the globe, the timeline (the family's days, back
 * through the years) and the calendar (the days that are coming) — and then the
 * Add button, at the end of the row.
 *
 * The order is the family's own order of interest: who we are, where we are,
 * what has happened, what is next. The Moments tab used to sit between the
 * last two — a wall of every picture anyone had ever shared, in upload order.
 * It went because nothing is looked for that way: a picture is looked for
 * through the person it is of or the day it belongs to, which is exactly what
 * the tree and the timeline already open.
 *
 * Admin controls are not a tab — they live in the burger menu, behind the
 * Admin mode switch.
 * Tabs with a `feature` only show in the modes that include it (Simple mode
 * keeps just the Add button — see app/experience.ts and the note in Shell below).
 * Your own profile, notifications and settings are panels that open over
 * whatever screen you are on (see Panels), reached from the burger menu.
 *
 * The rows themselves live in app/tabTips.ts, beside the sentence each tab says
 * about itself — one list, so a tab cannot be added without its description.
 */
const NAV = DOCK_TABS;

/**
 * The menu behind the burger: your profile, the language, settings — and, for
 * an administrator, the Admin mode switch and the way into Admin controls.
 * Nothing here is needed often, so nothing here is on screen until it is asked
 * for. Closes on Escape, on a click outside, and on choosing anything.
 */
function BurgerMenu({ onClose, onReport }: { onClose: () => void; onReport: () => void }) {
  const { t, lang, setLang } = useI18n();
  const repos = useRepos();
  const { me, actor, openPanel, openWelcome, canBeAdmin, adminMode, setAdminMode, pendingReviewCount } = useApp();
  const { navigate } = useRouter();
  const other = lang === 'en' ? 'he' : 'en';
  const go = (fn: () => void) => () => {
    fn();
    onClose();
  };
  return (
    <div className="burger-menu" role="menu" aria-label={t('nav.menu')}>
      {/* You, then how the app behaves, then which language, then the one way to
          grow the tree — everyday first, occasional last. */}
      <button type="button" role="menuitem" className="burger-item" data-assist="menu.profile" onClick={go(() => openPanel('profile'))}>
        {me ? <Avatar person={me} lang={lang} size="sm" /> : <Icon name="person" />}
        <span>{t('nav.me')}</span>
      </button>
      <button type="button" role="menuitem" className="burger-item" data-assist="menu.settings" onClick={go(() => openPanel('settings'))}>
        <Icon name="settings" />
        <span>{t('nav.settings')}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className="burger-item"
        data-assist="menu.language"
        lang={other}
        aria-label={t('lang.switchLabel')}
        onClick={go(() => {
          setLang(other);
          void repos.auth.updatePreferredLang(other);
        })}
      >
        <span className="burger-lang" aria-hidden="true">{other === 'he' ? 'עב' : 'EN'}</span>
        {/* The other language, in its own name — the shortest way to say what the item does. */}
        <span>{other === 'he' ? 'עברית' : 'English'}</span>
      </button>
      {/* Growing the tree happens here and nowhere else, so there is one answer
          to "how do I add someone" rather than four. */}
      {me && (
        <button type="button" role="menuitem" className="burger-item" data-assist="menu.addRelative" onClick={go(() => navigate(`/people/${me.id}/add`))}>
          <Icon name="people" />
          <span>{t('add.relative')}</span>
        </button>
      )}

      {/*
        * The welcome message, to read again. It comes up by itself once — and
        * again when the administrators change it — and after that this is where
        * it lives. Last of the everyday items because it is the least often
        * wanted, and named for what it is rather than for what it does: nobody
        * goes looking for "reopen the introduction".
        */}
      <button type="button" role="menuitem" className="burger-item" data-assist="menu.welcome" onClick={go(() => openWelcome('again'))}>
        <Icon name="billboard" />
        <span>{t('greeting.menu')}</span>
      </button>
      {/* Anyone can tell the people building the app what went wrong or what they wish it did. */}
      <button type="button" role="menuitem" className="burger-item" data-assist="menu.reportIssue" onClick={go(onReport)}>
        <Icon name="bulb" />
        <span>{t('issue.menu')}</span>
      </button>

      {/*
        * A Family Editor's way in.
        *
        * They have a queue to review, reports to answer and invitations to send — and
        * had no door to any of it: the only entry here was the administrator's, behind
        * a switch an editor does not have, so the screen could be reached by following
        * a notification and not otherwise. It is the same screen under the name it has
        * for them, and it is not violet: nothing an editor does there is an
        * administrator's power. (It shares the administrator's anchor, because nobody
        * ever has both buttons.)
        */}
      {!canBeAdmin && actor?.session.role === 'editor' && (
        <>
          <span className="burger-sep" role="none" />
          <button type="button" role="menuitem" className="burger-item" data-assist="menu.admin" onClick={go(() => navigate('/manage'))}>
            <Icon name="manage" />
            <span className="grow">{t('nav.manage')}</span>
            {pendingReviewCount > 0 ? <span className="badge">{pendingReviewCount}</span> : null}
          </button>
        </>
      )}

      {canBeAdmin && (
        <>
          <span className="burger-sep" role="none" />
          {/* Administrator territory, and it says so in the admin colour: the switch
              while it is on, and the way into the controls it unlocks. While the
              switch is off this account is an ordinary member everywhere in the app. */}
          <label className={`burger-item burger-switch ${adminMode ? 'is-on is-admin-control' : ''}`} data-assist="menu.adminMode">
            <Icon name="manage" />
            <span className="grow">{t('admin.mode')}</span>
            <input type="checkbox" checked={adminMode} onChange={(e) => setAdminMode(e.target.checked)} />
            <span className="switch" aria-hidden="true" />
          </label>
          {adminMode && (
            <button type="button" role="menuitem" className="burger-item is-admin-control" data-assist="menu.admin" onClick={go(() => navigate('/manage'))}>
              <Icon name="shield" />
              <span className="grow">{t('nav.admin')}</span>
              {pendingReviewCount > 0 ? <span className="badge">{pendingReviewCount}</span> : null}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** The burger (everything that is only occasionally needed) with the bell tucked under it. */
export function TopActions({ className = '' }: { className?: string }) {
  const { t } = useI18n();
  const { unreadCount, prominentUnreadCount, openPanel, panel, shabbatQuiet, has, adminMode } = useApp();
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const host = useRef<HTMLDivElement | null>(null);
  // Shabbat mode: the bell keeps quiet — no count, no nudge — until Shabbat is over.
  const showCount = !shabbatQuiet && unreadCount > 0;
  const prominent = !shabbatQuiet && prominentUnreadCount > 0;

  // A click anywhere else, or Escape, puts the menu away.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!host.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <div className={`top-actions ${className}`} ref={host}>
      <button
        type="button"
        className={`fab-icon ${open ? 'is-on' : ''} ${adminMode ? 'is-admin' : ''}`}
        data-assist="nav.menu"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.menu')}
        title={t('nav.menu')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="burger" />
        {adminMode ? <span className="dotmark is-admin" aria-hidden="true" /> : null}
      </button>
      {open && <BurgerMenu onClose={() => setOpen(false)} onReport={() => setReporting(true)} />}
      <ReportIssueDialog open={reporting} onClose={() => setReporting(false)} />
      {has('notifications') && (
        <button
          type="button"
          className={`fab-icon ${prominent ? 'has-prominent' : ''} ${panel === 'notifications' ? 'is-on' : ''} ${shabbatQuiet ? 'is-quiet' : ''}`}
          data-assist="nav.notifications"
          onClick={() => openPanel('notifications')}
          aria-label={t('nav.notifications')}
          title={shabbatQuiet ? t('shabbat.quietTitle') : t('nav.notifications')}
          aria-haspopup="dialog"
        >
          <Icon name={shabbatQuiet ? 'bellOff' : 'bell'} />
          {showCount ? <span className={`badge ${prominent ? 'is-star' : ''}`}>{unreadCount}</span> : null}
        </button>
      )}
    </div>
  );
}

export function Shell({ children, fullBleed }: { children: ReactNode; fullBleed?: boolean }) {
  const { t } = useI18n();
  const { path, state } = useRouter();
  const { online, pendingOps, reload, adding, openAdd, openPanel, closePanel, closeAdd, closeOverlays, openStory, openRecipe, has } = useApp();
  const repos = useRepos();
  const [justReconnected, setJustReconnected] = useState(false);
  // Storage could not be opened (rare, transient): say so instead of quietly showing a photo-less demo.
  const [storageOk, setStorageOk] = useState(true);
  useEffect(() => {
    setStorageOk(repos.sync.isStoragePersistent());
  }, [repos, path]);

  // Moving to another screen closes whatever was open over the old one — unless the navigation
  // asked for something (an old /me, /settings, /notifications or /stories/:id link lands here
  // with the panel or the story it wants).
  useEffect(() => {
    closeAdd();
    closeOverlays();
    const asked = state as { openPanel?: PanelKind; openStory?: string; openRecipe?: string } | null;
    if (asked?.openPanel) openPanel(asked.openPanel);
    else closePanel();
    // A link to a story (a notification, a bookmark) arrives as a request to open it here.
    if (asked?.openStory) openStory(asked.openStory);
    // And so does a link to a recipe — "Sarah wrote down Savta's fish".
    if (asked?.openRecipe) openRecipe(asked.openRecipe);
  }, [path, state, openPanel, closePanel, closeAdd, closeOverlays, openStory, openRecipe]);

  useEffect(() => {
    if (online && pendingOps > 0) {
      setJustReconnected(true);
      const timer = setTimeout(() => setJustReconnected(false), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [online, pendingOps]);

  const isActive = (to: string) => path === to || path.startsWith(`${to}/`);

  /*
   * Admin controls gives up the dock, and gets the corner for it.
   *
   * Its way out is the wedge in the bottom-start corner, and a wedge that size
   * needs the corner to itself — a phone leaves only 44–65px clear beside the
   * dock (measured at 360–430px wide). Handing it over is the right trade on
   * this screen in particular: Admin controls is somewhere you go and come back
   * from, not one of the places you live, and every tab in the dock is one press
   * away through the wedge anyway.
   */
  const wedgeScreen = path.startsWith('/manage');

  return (
    <div className={`shell ${fullBleed ? 'shell-immersive' : ''}`}>
      <div className="shell-body">
        {/*
          * Two corners, and each one means something.
          *
          * End: the burger and the bell — the app's own furniture, the things
          * you go looking for. Start: one button that means "I am stuck", which
          * is the only thing on screen you should be able to find without
          * looking for it. It is deliberately opposite the menu rather than
          * inside it: help you have to open a menu to reach is help you have to
          * already be coping to use.
          *
          * It follows the reading direction like everything else, so it is
          * top-left in English and top-right in Hebrew, and every screen's own
          * top row keeps clear of it with `--corner-start-w`.
          */}
        <AssistantButton className="assist-corner-float" />
        <TopActions className="top-actions-float" />

        {!storageOk && (
          <div className="sync-bar" role="alert">
            <Icon name="warning" />
            <span>{t('common.storageUnavailable')}</span>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => window.location.reload()}>
              {t('common.reloadPage')}
            </button>
          </div>
        )}
        {!online && (
          <div className="sync-bar" role="status">
            <Icon name="offline" />
            <span>{t('common.offline')}</span>
          </div>
        )}
        {online && justReconnected && pendingOps > 0 && (
          <div className="sync-bar online" role="status">
            <Icon name="wifi" />
            <span>{t('common.reconnected')}</span>
          </div>
        )}
        {online && pendingOps > 0 && !justReconnected && (
          <div className="sync-bar" role="status">
            <Icon name="cloudOff" />
            <span>{t('common.pendingCount', { count: pendingOps })}</span>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => {
                void repos.sync.replayPending().then(() => reload());
              }}
            >
              {t('common.retryNow')}
            </button>
          </div>
        )}

        <main className={`shell-main ${fullBleed ? 'full' : ''} ${wedgeScreen ? 'has-wedge' : ''}`} id="main" tabIndex={-1}>
          {children}
        </main>
      </div>

      {/* Floating dock: the whole app's navigation as pictures, at the bottom of every screen bar
          one (see `wedgeScreen`). The Add button is always the last thing in the row, so it never
          moves as tabs come and go. */}
      {/* What the tab you are on is for — until its × is pressed, and then never again (see TabTip). */}
      {!wedgeScreen && <TabTip />}
      {!wedgeScreen && (
      <nav className="dock" aria-label={t('nav.menu')}>
        {(() => {
          // Tabs the current mode includes. The Family Tree tab only earns its place when there are
          // other tabs to come back from: in Simple mode every other screen has a Back button to the
          // tree, so the dock is just the Add button.
          const modeTabs = NAV.filter((n) => n.feature && has(n.feature));
          const tabs = modeTabs.length ? [NAV[0], ...modeTabs] : [];
          type Item = { to: string; key: MessageKey; icon: string; badge: number };
          const toItem = (n: (typeof NAV)[number]): Item => ({ to: n.to, key: n.key, icon: n.icon, badge: 0 });
          const items: Item[] = tabs.map(toItem);
          const render = (n: Item) => (
            <Link key={n.to} to={n.to} className="dock-btn" data-assist={`dock.${n.to.slice(1)}`} ariaCurrent={isActive(n.to) ? 'page' : undefined} aria-label={t(n.key)} title={t(n.key)}>
              <Icon name={n.icon} />
              {n.badge > 0 ? <span className="badge">{n.badge}</span> : null}
            </Link>
          );
          return (
            <>
              {items.map(render)}
              <button
                type="button"
                className={`dock-add ${adding ? 'is-open' : ''}`}
                data-assist="dock.add"
                onClick={() => (adding ? closeAdd() : openAdd('menu'))}
                aria-label={t('add.tab')}
                title={t('add.tab')}
                aria-haspopup="dialog"
                aria-expanded={!!adding}
              >
                <Icon name="plus" />
              </button>
            </>
          );
        })()}
      </nav>
      )}

      <AddSheet />
      <Panels />
      <AssistantOverlay />
      {/* Last, so it paints over everything — and it portals itself into the
          top-layer dialog when one is open. See Spotlight. */}
      <Spotlight />
    </div>
  );
}

export function BrandMark() {
  return (
    <svg className="mark" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <circle cx="16" cy="8" r="4.5" fill="var(--primary)" />
      <circle cx="8" cy="23" r="4.5" fill="var(--memory)" />
      <circle cx="24" cy="23" r="4.5" fill="var(--memory)" />
      <path d="M16 12.5v4M16 16.5l-8 3M16 16.5l8 3" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/*
 * The page header and the empty state live in app/PageHeader.tsx, and are
 * re-exported here because every screen already imports them from the Shell.
 * The move keeps the Shell out of the import path of a screen the Shell itself
 * renders as a panel (the profile).
 */
export { EmptyState, PageHeader } from './PageHeader';

export { Banner };
